import { directoryClient } from 'shared/domain/directory/directoryClient';
import type { BreadcrumbItem } from 'shared/domain/directory/directoryContract';
import { useEffect, useState } from 'react';
import { logError } from 'setup/logger/logError';
import { areDirectoryIdsEqual, isRootDirectory } from 'shared/domain/directory/directoryIds';

interface DirectoryPathState {
  directoryId: string | undefined;
  path: BreadcrumbItem[];
}

/**
 * Ancestor path of a documentation directory, fetched on demand - for views
 * that only need a folder path (map pickers) and must not load the whole
 * documentation store or touch the grid's current directory.
 */
export function useDirectoryPath(directoryId: string | undefined): {
  path: BreadcrumbItem[];
  isLoading: boolean;
} {
  const [loaded, setLoaded] = useState<DirectoryPathState>({
    directoryId: undefined,
    path: [],
  });

  useEffect(() => {
    if (isRootDirectory(directoryId)) {
      return;
    }
    // Drop the response if the directory changed or the view unmounted.
    let isCurrentRequest = true;
    directoryClient.getBreadcrumbs(directoryId ?? '')
      .then((path) => {
        if (isCurrentRequest) {
          setLoaded({ directoryId, path });
        }
      })
      .catch((error: unknown) => {
        logError('Error fetching directory path', error);
        if (isCurrentRequest) {
          setLoaded({ directoryId, path: [] });
        }
      });
    return () => {
      isCurrentRequest = false;
    };
  }, [directoryId]);

  if (isRootDirectory(directoryId)) {
    return { path: [], isLoading: false };
  }
  const isLoaded = areDirectoryIdsEqual(loaded.directoryId, directoryId);
  return { path: isLoaded ? loaded.path : [], isLoading: !isLoaded };
}
